import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { useLatestRef } from '../../../hooks/useLatestRef'
import type { PartialRange } from '../../../types'

// Same granularity as the slider in the set dialog.
const STEP_MIN = 5

export type TrimEdge = 'start' | 'end'

export interface TrimHandleProps {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void
  onPointerUp: () => void
  onPointerCancel: () => void
}

export interface SetTrim {
  /** The live window while trimming, else null. */
  draft: [number, number] | null
  start: () => void
  stop: () => void
  handleProps: (edge: TrimEdge) => TrimHandleProps
}

/**
 * Drag-to-trim for a set card. Entered explicitly (long-press) so the card
 * edges are never grabbable while the schedule is being scrolled. Each handle
 * release saves immediately, so leaving trim mode — a tap anywhere, Escape, or
 * scrolling away — never loses anything.
 */
export function useSetTrim({
  measureRef,
  withinRefs,
  duration,
  range,
  onCommit,
}: {
  /** The box the range is drawn in; pointer → minutes is measured against it. */
  measureRef: RefObject<HTMLElement | null>
  /** A pointerdown inside any of these doesn't end trim mode. */
  withinRefs: RefObject<HTMLElement | null>[]
  duration: number
  range: PartialRange | null
  onCommit: (range: PartialRange | null) => void
}): SetTrim {
  const [draft, setDraftState] = useState<[number, number] | null>(null)
  const draftRef = useRef(draft)
  const dragging = useRef<TrimEdge | null>(null)
  const setDraft = (next: [number, number] | null) => {
    draftRef.current = next
    setDraftState(next)
  }
  const trimming = draft !== null
  const within = useLatestRef(withinRefs)

  useEffect(() => {
    if (!trimming) return
    const swallowClick = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }
    const onDown = (e: PointerEvent) => {
      if (within.current.some(r => r.current?.contains(e.target as Node))) return
      draftRef.current = null
      setDraftState(null)
      // The tap that dismisses trim mode shouldn't also land on what it hit.
      window.addEventListener('click', swallowClick, { capture: true, once: true })
      window.setTimeout(() => window.removeEventListener('click', swallowClick, { capture: true }), 600)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      draftRef.current = null
      setDraftState(null)
    }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [trimming, within])

  const commit = () => {
    const d = draftRef.current
    if (!d) return
    const [startMin, endMin] = d
    const current = range ? [range.startMin, range.endMin] : [0, duration]
    if (startMin === current[0] && endMin === current[1]) return
    onCommit(startMin === 0 && endMin === duration ? null : { startMin, endMin })
  }

  const endDrag = () => {
    if (dragging.current === null) return
    dragging.current = null
    commit()
  }

  return {
    draft,
    start: () => setDraft(range ? [range.startMin, range.endMin] : [0, duration]),
    stop: () => setDraft(null),
    handleProps: edge => ({
      onPointerDown: e => {
        e.stopPropagation()
        e.preventDefault()
        e.currentTarget.setPointerCapture(e.pointerId)
        dragging.current = edge
      },
      onPointerMove: e => {
        const d = draftRef.current
        const rect = measureRef.current?.getBoundingClientRect()
        if (dragging.current !== edge || !d || !rect || rect.height === 0) return
        const raw = ((e.clientY - rect.top) / rect.height) * duration
        // Let the last step reach the set's real end when it isn't on the grid.
        const snapped = raw >= duration - STEP_MIN / 2 ? duration : Math.round(raw / STEP_MIN) * STEP_MIN
        const next: [number, number] = edge === 'start'
          ? [Math.max(0, Math.min(snapped, d[1] - STEP_MIN)), d[1]]
          : [d[0], Math.min(duration, Math.max(snapped, d[0] + STEP_MIN))]
        if (next[0] === d[0] && next[1] === d[1]) return
        navigator.vibrate?.(5)
        setDraft(next)
      },
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    }),
  }
}
